import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCreateDialog } from "../hooks/useCreateDialog";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { getWithRetry, postWithRetry, isUnknownAnswer } from "../lib/fetchWithRetry";
import { markSurfaceNotificationsRead } from "../lib/notificationsRead";
import LoadFailureNote from "../components/LoadFailureNote";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  Plus, Target as TargetIcon, CheckCircle2, Trash2, Sparkles, Loader2, Rocket, Trophy,
  Wrench, Radar, Dumbbell, Brain, Check, RotateCw, Compass, NotebookPen, Pin, Users,
} from "lucide-react";
import { toast } from "sonner";
import PlayerIdentity from "./PlayerIdentity";
import Reflections from "./Reflections";
import { AI_COACH_ENABLED, newUi } from "../lib/featureFlags";
import { GOAL_STATUSES, normalizeGoalStatus, goalStatusMeta } from "../lib/goalStatus";
import { computePlanGate } from "../lib/planGate";
import { GOAL_CATEGORY_LABELS } from "../lib/goalCategory";
import PlanScreen from "../components/PlanScreen";
import PlanMilestonesScreen from "../components/PlanMilestonesScreen";
import PlanGoalsList from "../components/PlanGoalsList";
import NewGoalSheet from "../components/NewGoalSheet";
import DevelopmentChrome from "../components/DevelopmentChrome";
import PlanComingUp from "../components/PlanComingUp";
import { useAddedMatch } from "../hooks/useAddedMatchNotice";
import PlanMatchScreen from "../components/PlanMatchScreen";
import PlanCoachGame from "../components/PlanCoachGame";
import PlanMoreFolds from "../components/PlanMoreFolds";
import PlayerCoachFeedback from "../components/PlayerCoachFeedback";
import { SecondaryAction, SectionStrip, SectionPanel } from "../components/kit";
import { resolvePlanSection } from "../lib/planSections";
import { NOTE_CATEGORIES } from "../lib/noteCategories";

// Issue #764 review round 1, F4 — the LABEL half of this map now comes from
// `lib/goalCategory.js`, shared with `PlanMilestonesScreen.jsx`'s own read
// of the same enum. The icon and colour stay here: this list page is the
// only caller that draws either.
const CATEGORIES = {
  technical: { label: GOAL_CATEGORY_LABELS.technical, Icon: Wrench, color: "#0B2E59" },
  tactical:  { label: GOAL_CATEGORY_LABELS.tactical, Icon: Radar, color: "#D4A017" },
  physical:  { label: GOAL_CATEGORY_LABELS.physical, Icon: Dumbbell, color: "#10B981" },
  mental:    { label: GOAL_CATEGORY_LABELS.mental, Icon: Brain, color: "#8B5CF6" },
  general:   { label: GOAL_CATEGORY_LABELS.general, Icon: TargetIcon, color: "#64748B" },
  batting:   { label: GOAL_CATEGORY_LABELS.batting, Icon: TargetIcon, color: "#0B2E59" },
  bowling:   { label: GOAL_CATEGORY_LABELS.bowling, Icon: TargetIcon, color: "#D4A017" },
  fielding:  { label: GOAL_CATEGORY_LABELS.fielding, Icon: TargetIcon, color: "#84C5FF" },
};


// Issue #197: STATUSES/normalizeStatus used to be defined locally here.
// Now sourced from lib/goalStatus.js — the single shared vocabulary also
// consumed by CoachPortal.jsx (the coach-write side of the same data).

// Issue #1166 (D215.1, D215.2): a player's page alone. App.js sends a coach
// or admin to /coach-portal before this mounts; the same fact is checked here
// too, so that a future route change cannot bring the shipped coach/admin
// Development page back. It renders nothing and asks the API for nothing for
// anyone else. (The inner component holds every hook, so this check is the
// only early return.)
export default function DevelopmentPlan() {
  const { user } = useAuth();
  if (user?.role !== "player") return null;
  return <PlayerDevelopmentPlan />;
}

function PlayerDevelopmentPlan() {
  // #1150 (D349): arrived from a saved "How did it go?" - the board's pill, once.
  const addedMatch = useAddedMatch();
  const [sp, setSp] = useSearchParams();
  const [goals, setGoals] = useState([]);
  const [notes, setNotes] = useState([]);
  // `null` = not yet known (distinct from `false`) so the feedback tab
  // doesn't flash "no coach yet" before /players/me has answered — see
  // CoachFeedbackPanel below.
  const [hasCoach, setHasCoach] = useState(null);
  // Issue #271: "we don't know" is a third state, distinct from "you have
  // none". Both reads below used to resolve into the same `[]` / `false`
  // that a genuinely-empty account produces.
  const [goalsFailed, setGoalsFailed] = useState(false);
  // Issue #763's own gate (`lib/planGate.js`) needs a fact `goals` itself
  // cannot carry: `goals` starts `[]` before the first `GET /goals`
  // response lands, indistinguishable both from a genuinely empty account
  // AND from a caller this route was never meant for.
  //
  // `goalsAnswered` is set ONLY inside the SUCCESS branch below, never in
  // `.catch` — found live, on the first flag-on responsive-harness run.
  // `goalsFailed: false` plus `goals: []` together read exactly like "a
  // real 200 confirmed zero goals" to a gate that only checked those two. An
  // earlier version of this file's gate did exactly that, when a coach could
  // still reach this page and `GET /goals` with no `player_id` answered them
  // a deterministic 400 (#1166 closed that road). `goalsAnswered` names the
  // fact the gate actually needs — "a genuine 200 landed" — so any failure
  // can never be mistaken for a confirmed empty plan.
  const [goalsAnswered, setGoalsAnswered] = useState(false);
  const [feedbackFailed, setFeedbackFailed] = useState(false);
  // Issue #763 — the gate's OWN one-shot "?new=1" read, via its OWN
  // `useCreateDialog()` instance, DELIBERATELY NOT shared with
  // `NewGoalDialog`'s (below, unchanged, self-contained). An earlier
  // version lifted this into one shared instance passed down as a prop —
  // wrong: `NewGoalDialog`'s own `open` also flips true on every
  // `dev-new-goal` CLICK (not just the URL), and since this gate's
  // `createOpen` bypasses `onGoalTab` entirely (see `lib/planGate.js`'s
  // own header), a click on ANY tab where a plan already exists swapped
  // the WHOLE PAGE to `PlanScreen` mid-interaction — `NewGoalDialog`'s own
  // `[role="dialog"]` never got a chance to open. Found live: broke every
  // `/development` cell in the responsive harness. Two independent
  // `useCreateDialog()` calls both read the SAME underlying
  // `useSearchParams()` state (react-router shares it across call sites in
  // one tree) and each strips `?new=1` itself — idempotent if the other
  // already did — so the deep-link contract (`journey 14`'s "the create
  // param is spent, not sticky") still holds for both.
  const [planCreateIntent, setPlanCreateIntent] = useCreateDialog();
  const load = () =>
    getWithRetry("/goals")
      .then((r) => { setGoals(r.data || []); setGoalsFailed(false); setGoalsAnswered(true); })
      .catch((e) => {
        // Was an uncaught `.then()` chain: the rejection went nowhere, the
        // goal list stayed `[]`, and GoalGrid told the player "No
        // short-term goals yet. Some ideas: …" about goals it had never
        // been sent.
        //
        // Gated on `isUnknownAnswer` because a refused request (a 4xx) is
        // an ANSWER, not an unknown. Offering a Try again button for it
        // would promise a recovery that can never happen.
        setGoalsFailed(isUnknownAnswer(e));
        console.error("DevelopmentPlan: failed to load /goals", e);
      });
  // Issue #81: GET /notes is already scoped to the caller's own player
  // profile with no role gate for reads (api/src/routes/notes.ts) — a
  // player has always been able to read their own feedback via the API,
  // there was just no UI surface for it. `has_coach` comes from the same
  // /players/me every player-facing page already calls (see players.ts) —
  // it's what lets the empty state below tell "no coach yet" apart from
  // "coach hasn't left feedback yet" without a new endpoint.
  //
  // Issue #271: `.catch(() => setHasCoach(false))` converted a failed
  // request into the assertion "you don't have a coach" — the single most
  // discouraging thing this page can say, told to a player who does. Both
  // legs now report failure as failure; the panel renders that instead of
  // choosing between two empty states it has no evidence for.
  const loadFeedback = () =>
    Promise.all([getWithRetry("/notes"), getWithRetry("/players/me")])
      .then(([n, p]) => {
        setNotes(n.data || []);
        setHasCoach(!!p.data.has_coach);
        setFeedbackFailed(false);
      })
      .catch((e) => {
        // Same coach-reachability reasoning as `load` above. On a
        // deterministic 4xx, leave `hasCoach` false so the panel keeps its
        // existing (pre-#271) rendering rather than inventing a transport
        // problem that did not happen.
        const unknown = isUnknownAnswer(e);
        setHasCoach(unknown ? null : false);
        setFeedbackFailed(unknown);
        console.error("DevelopmentPlan: failed to load coach feedback", e);
      });
  useEffect(() => { load(); loadFeedback(); }, []);

  const short = goals.filter((g) => (g.horizon || "short") === "short");
  const long = goals.filter((g) => g.horizon === "long");

  // AI Development Plan tab is flagged off by default (spec §7/D6, §8.1;
  // issue #56) — per-token LLM cost, budget decision pending. A stale
  // `?tab=ai` in the URL (bookmark, back-button) falls back to the default
  // tab rather than rendering a tab that no longer has a trigger.
  //
  // Issue #464 splits the read in two: `chosenTab` is the tab the player
  // ASKED for (null when they just landed on `/development`), `tab` is the
  // one this page RENDERS. Only the first may clear notifications — see the
  // effect below.
  const chosenTab = sp.get("tab");
  const requestedTab = chosenTab || "short";
  const tab = requestedTab === "ai" && !AI_COACH_ENABLED ? "short" : requestedTab;
  // #1164 (flag on only): the same `?tab=` read as a section, a fold in More and
  // a goal group. `tab` and `chosenTab` stay as they are for the flag-off page
  // and for the effects below.
  const view = resolvePlanSection(chosenTab, { aiEnabled: AI_COACH_ENABLED });
  const groupRefs = { short: useRef(null), long: useRef(null) };
  // `?tab=short|long` is Goals with that group scrolled to, once the goals are in.
  useEffect(() => {
    const el = view.group && goalsAnswered ? groupRefs[view.group].current : null;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.group, view.section, goalsAnswered]);

  // Issue #184 / SPEC-pilot-polish.md §3.4 item 1: "landing on the tab" is
  // the whole gesture — there's no per-note read affordance anywhere in
  // this read-only panel (CoachFeedbackPanel below), so that's the only
  // granularity the UI actually offers. Fires on every transition INTO
  // "feedback" (both a click on the tab trigger via onValueChange above,
  // and a direct deep link from the Dashboard tile landing here with
  // ?tab=feedback already set), not on every render — the `[tab]` dep
  // means this effect is inert unless the tab identity itself changes.
  useEffect(() => {
    if (tab === "feedback") {
      // Issue #271, one of the two PROVEN sites: a failing run's server log
      // showed `POST /api/players/me/coach-feedback-seen 503`, the failure
      // was swallowed, and an unread marker survived over feedback the
      // player was looking at. So the write RETRIES, bounded — and it is
      // idempotent by construction (it stamps a watermark to "now", so
      // re-sending cannot double-apply), which is what makes the state
      // self-correcting rather than merely hopeful.
      //
      // Issue #189 removed what used to sit in front of this call: a
      // `cc:coach-feedback-seen` window event that told Layout.jsx's bell
      // to clear its badge optimistically. The bell no longer reads this
      // watermark at all — it has its own (`users.notifications_seen_at`,
      // cleared by opening the panel) and it now counts coach-assigned
      // goals and coach-scheduled sessions too, so clearing it here would
      // dismiss notifications the player has not seen. The event had no
      // other listener, so it went with the reader that needed it; the
      // optimistic-first pattern itself lives on in
      // NotificationsBell.jsx, where it is still the right shape.
      //
      // What this watermark still drives: the Dashboard's Coach feedback
      // tile, which reads it on its own next mount. Worst case (every
      // retry fails) the tile keeps saying unread until the next visit to
      // this tab stamps it again — a stale "unread" over feedback that has
      // been read, which is the harmless direction of that error. No
      // toast: there is no action for the user here.
      postWithRetry("/players/me/coach-feedback-seen")
        .catch((e) => console.error("DevelopmentPlan: failed to stamp coach-feedback-seen", e));
    }

    /**
     * Issue #417, the other half of "looked at it … by going to it on the
     * platform" — the BELL's per-item read state, which is a different
     * mechanism from the watermark above and stays that way.
     *
     * Landing on Coach Feedback clears the bell's `coach_feedback` items;
     * landing on either goals tab clears its `coach_goal` items (the
     * surface table lives in `lib/notificationsRead.js`, deliberately as
     * the inverse of the API's link table, with a test that asserts the
     * two agree). Before this, walking to the goal your coach set you left
     * the badge lit until you opened the bell — which is exactly what
     * Martin reported.
     *
     * NOT folded into the call above, even though both fire on the same
     * landing. They write different state answering different questions
     * (`schema.ts` calls merging them a defect), and this one covers goals
     * the feedback watermark knows nothing about.
     *
     * ISSUE #464 — `chosenTab`, not `tab`. `tab` carries the default, so
     * passing it meant that merely opening `/development` consumed every
     * `coach_goal` item before the player had seen the goals list, and the
     * bell never showed a number for one. An absent `?tab=` clears nothing
     * (`typesForSurface` tolerates a null tab on purpose); clicking the
     * Short-term tab writes `?tab=short` and does clear, which is the
     * "going to it on the platform" gesture #417 was asked for.
     */
    markSurfaceNotificationsRead("development", chosenTab);
  }, [tab, chosenTab]);

  // Issue #763 (D98) — behind `newUi("plan")`, the redrawn create-a-plan
  // screen replaces THIS WHOLE PAGE for a PLAYER with no goal at all, or on
  // a `?new=1` deep link — see `lib/planGate.js`'s own header for the full
  // story of each guard: the role (fix round B1 — a coach/admin never
  // reaches this screen by any path, D92; since #1166 the whole page is a
  // player's, so the gate is handed `isPlayer: true`), `goalsAnswered` (not merely
  // `goals.length === 0`, since a coach's 400 must never read as "confirmed
  // empty"), `onGoalTab` (found live: an earlier version broke journey
  // 13's `?tab=identity` deep link by swallowing non-goal tabs too), and
  // goal counting itself — RR1 (Andreas's ruling, second fix round): EVERY
  // goal counts, `completed` included, so a player with only completed
  // goals keeps today's page and can still reach their own history.
  // `createOpen` is fed from THIS page's own `useCreateDialog()` instance,
  // deliberately never `NewGoalDialog`'s — see `planCreateIntent`'s own
  // comment above. A player who already has a goal, reached with no
  // `?new=1`, still sees today's page below, unaffected — #764 (the
  // milestones screen) is what replaces it next.
  const { showNewPlan } = computePlanGate({
    planScreenOn: newUi("plan"),
    isPlayer: true,
    createOpen: planCreateIntent,
    // #1164: on the new look, any value that opens Coming up or Goals (an absent,
    // unknown or AI-off `?tab=` included) is a goal tab, as bare /development is.
    onGoalTab: newUi("dev-chrome")
      ? view.section === "coming-up" || view.section === "goals"
      : chosenTab == null || chosenTab === "short" || chosenTab === "long",
    goalsAnswered,
    goals,
  });

  // Issue #764 (D166 M1/M2/M7/M12) — the milestones step is a per-goal
  // drill-down, not a new route: `?milestone=<goal_id>` on THIS SAME page,
  // the same "spend a query param, don't own a route" shape #763's own
  // `?new=1` already uses. `milestonesOn` gates BOTH the tap affordance on
  // a goal card (below, threaded into `GoalGrid`/`GoalCard`) and the step
  // itself here — `newUi("plan-milestones")` already requires
  // `newUi("plan")` (D166 M12, `lib/featureFlags.js`). D166 M7 (player-only)
  // is now held by the page itself: it renders for a player and no one else.
  const milestonesOn = newUi("plan-milestones");
  // Issue #940 (D175 P2/P4) — the `/development` chrome slice;
  // `newUi("dev-chrome")` itself already requires `plan` too
  // (`lib/featureFlags.js`, D175 P2). D175 P4 (coaches and admins never see
  // it) is now the page's own player-only rule above.
  const devChromeOn = newUi("dev-chrome");
  const milestonesGoalId = sp.get("milestone");
  const milestoneGoal = milestonesGoalId
    ? goals.find((g) => String(g.goal_id) === milestonesGoalId) || null
    : null;
  // Reachable while `goalsAnswered` is still false (a fresh mount / a
  // reload with `?milestone=` already on the URL — the two commentary
  // states `brief-764.md` asks for, `loading` and `failed-to-load`) or
  // once a genuine 200 has resolved the goal. A resolved list that does
  // NOT contain this id (a stale link, a deleted goal) falls through to
  // today's page below rather than a fourth, undrawn error state — not
  // ruled anywhere, so the narrowest defensible reading is chosen: no
  // silent partial screen, no invented copy.
  const showMilestones = milestonesOn && !!milestonesGoalId && (!goalsAnswered || goalsFailed || !!milestoneGoal);
  const openMilestones = (goalId) => {
    const n = new URLSearchParams(sp);
    n.set("milestone", String(goalId));
    setSp(n);
  };
  const closeMilestones = () => {
    const n = new URLSearchParams(sp);
    n.delete("milestone");
    setSp(n);
  };

  // Issue #1149 — Plan a match is one more step of this page, the way
  // `?milestone=` is: `?plan=match`, no route of its own. Flag-gated with the
  // chrome it hangs off (`dev-chrome` requires `plan`), so with either off the
  // param is simply ignored and the page is exactly today's. It wins over the
  // create-a-plan screen: a player with no goal yet can still plan a match.
  const planMatchOn = devChromeOn && sp.get("plan") === "match";
  const closePlanMatch = () => {
    const n = new URLSearchParams(sp);
    n.delete("plan");
    setSp(n);
  };
  // Issue #1319 (D302) — a coach's game is one more step of this page:
  // `?game=<id>`, opened from its row in Coming up or from the bell. It needs
  // the chrome and the Pick XI flag; with either off the param is ignored and
  // the page is exactly today's. Player only (this page is).
  const coachGameId = devChromeOn && newUi("pick-xi") ? sp.get("game") : null;
  const closeCoachGame = () => {
    const n = new URLSearchParams(sp);
    n.delete("game");
    setSp(n);
  };
  // Coming up (#1149): the chrome's own "Goals" opens the create-a-plan
  // screen through this page's `?new=1` intent; on that screen itself the
  // goal field is already on show, so Goals just takes focus there.
  const comingUp = (onAddGoal, emptyCard) => (devChromeOn ? (
    <PlanComingUp goals={goals} forRole="player" onAddGoal={onAddGoal} emptyCard={emptyCard} added={addedMatch} />
  ) : null);

  if (coachGameId) {
    return <PlanCoachGame gameId={coachGameId} onBack={closeCoachGame} />;
  }

  if (planMatchOn) {
    return <PlanMatchScreen onBack={closePlanMatch} onSaved={closePlanMatch} />;
  }

  if (showMilestones) {
    return (
      <PlanMilestonesScreen
        goal={milestoneGoal}
        loading={!goalsAnswered && !goalsFailed}
        failed={goalsFailed}
        onRetry={load}
        onBack={closeMilestones}
        onGoalChanged={devChromeOn ? load : undefined}
        onGoalDeleted={devChromeOn ? async () => { await load(); closeMilestones(); } : undefined}
      />
    );
  }

  if (showNewPlan) {
    return (
      <PlanScreen
        onCreated={async () => {
          // Reload the goals list, THEN close the create intent — both
          // must land before this returns, since `save()` in PlanScreen.jsx
          // awaits this whole function before resetting its own local
          // state (its own safety-net comment explains why).
          await load();
          setPlanCreateIntent(false);
        }}
        // Issue #763 fix round, F1 (RR2 — second fix round — scopes WHEN
        // this fires) — a player who ALREADY has a goal and arrived via
        // `?new=1` (the Dashboard chooser's own `withCreate("/development")`
        // link) had no way off this screen except browser Back:
        // `createOpen` wins the gate outright, and nothing else ever
        // cleared it. Tapping the now-current "Plan" chip
        // (`kit/DestinationRow.jsx`'s own `onCurrentClick`, opt-in) clears
        // the intent instead of navigating anywhere — the gate then
        // re-settles on its own next render: false for this player
        // (they have a goal), so today's page renders.
        //
        // RR2 — passed ONLY when `planCreateIntent` is actually true (a
        // real `?new=1` to clear). For a genuine zero-goal player, this
        // screen shows through the AUTO-SWAP branch instead
        // (`onGoalTab && goalsAnswered && goals.length === 0`, no
        // `createOpen` involved) — tapping "Plan" there would clear a
        // create intent that was never set, land back on the exact same
        // gate result, and do nothing. `undefined` in that case, so
        // `DestinationRow`'s current chip stays the plain, non-interactive
        // `<span>` it always was — no `role="button"`, no cursor, no
        // keyboard handler for a tap that has nothing to do.
        onCurrentDestinationTap={planCreateIntent ? () => setPlanCreateIntent(false) : undefined}
        comingUp={comingUp(() => document.querySelector('[data-testid="goal-target"]')?.focus(), false)}
      />
    );
  }

  // Issue #940 (D175) — flag off: today's page, unchanged. Kept as an early return with the ORIGINAL JSX untouched
  // (rather than threading a condition through one shared block) so the
  // flag-off leg stays byte-identical in behaviour, not merely "close" —
  // every acceptance criterion below is checked against THIS return, not
  // against how the flag-on branch happens to be written.
  if (!devChromeOn) {
    return (
      <div className="space-y-6" data-testid="development-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Structured improvement</p>
            <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">Development Plan</h1>
            {/* Issue #188: "AI-crafted objectives" sold the AI Development
                Plan tab, which AI_COACH_ENABLED keeps off by default (issue
                #56) — the header promised a tab the page does not render.
                The four dimensions are real (CATEGORIES above). */}
            <p className="text-slate-500 mt-1">Set the goals that give a season its direction — technical, tactical, physical and mental.</p>
          </div>
          <NewGoalDialog onCreated={load} />
        </div>

        <Tabs value={tab} onValueChange={(v) => { const n = new URLSearchParams(sp); n.set("tab", v); setSp(n, { replace: true }); }} data-testid="dev-tabs">
          <TabsList>
            <TabsTrigger value="identity" data-testid="tab-identity"><Compass size={14} className="mr-1.5" /> Identity &amp; Vision</TabsTrigger>
            {/* Issue #214: sits next to Identity & Vision because the two are
                the pair being told apart — who you are, then how it's actually
                going. This is the app's widest tab row and a fifth trigger
                makes it wider still; TabsList's `[justify-content:safe_center]`
                (issue #200) + `overflow-x-auto` (issue #209) is what keeps the
                leading trigger reachable at 390px, and the harness measures
                exactly that. */}
            <TabsTrigger value="reflections" data-testid="tab-reflections"><Brain size={14} className="mr-1.5" /> Reflections</TabsTrigger>
            <TabsTrigger value="short" data-testid="tab-short">Short-Term ({short.length})</TabsTrigger>
            <TabsTrigger value="long" data-testid="tab-long">Long-Term ({long.length})</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback"><NotebookPen size={14} className="mr-1.5" /> Coach Feedback ({notes.length})</TabsTrigger>
            {AI_COACH_ENABLED && (
              <TabsTrigger value="ai" data-testid="tab-ai"><Sparkles size={14} className="mr-1.5" /> AI Development Plan</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="identity" className="mt-6"><PlayerIdentity /></TabsContent>
          <TabsContent value="reflections" className="mt-6"><Reflections /></TabsContent>

          <TabsContent value="short" className="mt-6">
            <GoalGrid goals={short} onReload={load} horizon="short" failed={goalsFailed} onOpenMilestones={milestonesOn ? openMilestones : undefined} />
          </TabsContent>
          <TabsContent value="long" className="mt-6">
            <GoalGrid goals={long} onReload={load} horizon="long" failed={goalsFailed} onOpenMilestones={milestonesOn ? openMilestones : undefined} />
          </TabsContent>
          <TabsContent value="feedback" className="mt-6"><CoachFeedbackPanel notes={notes} hasCoach={hasCoach} failed={feedbackFailed} onRetry={loadFeedback} /></TabsContent>
          {AI_COACH_ENABLED && (
            <TabsContent value="ai" className="mt-6"><AiPlanPanel onSaved={load} /></TabsContent>
          )}
        </Tabs>
      </div>
    );
  }

  // Issue #940 (D175) — the flag-on leg. `DevelopmentChrome` owns the navy
  // TopBar, D92's DestinationRow and the header; no shipped header renders
  // here.
  //
  // Issue #1164 (D219, D233.5, D243) — three sections under the ribbon,
  // Coming up, Goals and More, replace the five chrome tabs. The URL keeps its
  // one `?tab=` parameter (`lib/planSections.js` maps every old value to a
  // section, a fold or a goal group). Strip taps and fold toggles write it with
  // `replace`. Which section opens is the URL's alone (D254: no memory), so a
  // bare `/development` is always Coming up. "New goal" (D175 P3) lives in
  // Goals only, as a navy `SecondaryAction`.
  const setTab = (value) => {
    const n = new URLSearchParams(sp);
    n.set("tab", value);
    setSp(n, { replace: true });
  };
  const newGoalTrigger = (
    <SecondaryAction variant="navy-outline" data-testid="dev-new-goal">
      <Plus size={18} strokeWidth={2} aria-hidden="true" style={{ marginRight: 8 }} />
      New goal
    </SecondaryAction>
  );
  const SECTION_ITEMS = [
    { key: "coming-up", label: "Coming up", testId: "plan-section-coming-up" },
    { key: "goals", label: "Goals", testId: "plan-section-goals" },
    { key: "more", label: "More", testId: "plan-section-more" },
  ];
  const SECTION_TITLES = { "coming-up": "Coming up", goals: "Goals", more: "More" };
  const folds = [
    { key: "reflections", title: "Reflections", sub: "Your log of matches, training and notes", body: <Reflections /> },
    {
      key: "feedback",
      title: feedbackFailed || hasCoach === null ? "Coach feedback" : `Coach feedback \u00b7 ${notes.length}`,
      sub: "What your coach has written to you",
      body: <PlayerCoachFeedback notes={notes} hasCoach={hasCoach} failed={feedbackFailed} onRetry={loadFeedback} />,
    },
    { key: "identity", title: "Identity & vision", sub: "Who you are as a player, and where you're going", body: <PlayerIdentity appearance="r5" /> },
  ];
  if (AI_COACH_ENABLED) {
    folds.push({ key: "ai", title: "AI Development Plan", body: <AiPlanPanel onSaved={load} /> });
  }
  const goalGridProps = { onReload: load, failed: goalsFailed, chrome: true, onOpenMilestones: milestonesOn ? openMilestones : undefined };

  let body;
  if (view.section === "goals") {
    // #1219 (D282, D283): the board's cards and the kit New goal sheet. With the
    // milestones step off a card has nowhere to open, and Update and Delete live in
    // the opened goal, so that one leg keeps the shipped cards and dialog.
    body = milestonesOn ? (
      <div className="flex flex-col" style={{ gap: 14 }} data-testid="plan-goals">
        <NewGoalSheet onCreated={load} />
        {goalsAnswered || goalsFailed ? (
          <PlanGoalsList goals={goals} failed={goalsFailed} onRetry={load} onOpenMilestones={openMilestones} groupRefs={groupRefs} />
        ) : null}
      </div>
    ) : (
      <div className="flex flex-col" style={{ gap: 14 }} data-testid="plan-goals">
        <NewGoalDialog onCreated={load} trigger={newGoalTrigger} />
        {goalsFailed ? (
          <GoalGrid goals={short} horizon="short" {...goalGridProps} />
        ) : goalsAnswered ? (
          <>
            {short.length > 0 && (
              <>
                <GoalGroupHeading groupRef={groupRefs.short} testId="plan-goals-group-short">Short-term · {short.length}</GoalGroupHeading>
                <GoalGrid goals={short} horizon="short" {...goalGridProps} />
              </>
            )}
            {long.length > 0 && (
              <>
                <GoalGroupHeading groupRef={groupRefs.long} testId="plan-goals-group-long">Long-term · {long.length}</GoalGroupHeading>
                <GoalGrid goals={long} horizon="long" {...goalGridProps} />
              </>
            )}
          </>
        ) : null}
      </div>
    );
  } else if (view.section === "more") {
    body = <PlanMoreFolds folds={folds} openKey={view.fold} onToggle={(key) => setTab(key === view.fold ? "more" : key)} />;
  } else {
    body = comingUp(() => setPlanCreateIntent(true), true);
  }

  return (
    <div data-testid="development-page">
      <DevelopmentChrome
        sections={<SectionStrip data-testid="plan-sections" idPrefix="plan-sections" items={SECTION_ITEMS} currentKey={view.section} onChange={setTab} />}
        title={SECTION_TITLES[view.section]}
      >
        <SectionPanel idPrefix="plan-sections" currentKey={view.section} className="flex flex-col" style={{ gap: 14 }}>
          {body}
        </SectionPanel>
      </DevelopmentChrome>
    </div>
  );
}

function GoalGroupHeading({ groupRef, testId, children }) {
  return (
    <h2
      ref={groupRef}
      data-testid={testId}
      style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}
    >
      {children}
    </h2>
  );
}

// ---------------- Goal grid ----------------
function GoalGrid({ goals, onReload, horizon, failed, onOpenMilestones, chrome = false }) {
  const active = goals.filter((g) => normalizeGoalStatus(g.status) !== "completed");
  const done = goals.filter((g) => normalizeGoalStatus(g.status) === "completed");

  // Issue #271: checked BEFORE the empty state, because a failed load and a
  // genuinely empty plan are the same `[]` and only one of them is safe to
  // say out loud.
  if (failed) return (
    <LoadFailureNote what="your development plan" testId={`goals-error-${horizon}`} onRetry={onReload} />
  );

  if (goals.length === 0) return (
    <div className="text-center py-16">
      <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "#D4A01720" }}>
        {horizon === "short" ? <Rocket size={22} color="#D4A017" /> : <Trophy size={22} color="#D4A017" />}
      </div>
      <p className="text-slate-500 mb-4">
        {horizon === "short" ? "No short-term goals yet. Some ideas: Improve batting average, Take more catches, Lower bowling economy."
          : "No long-term goals yet. Some ideas: Make First XI, Represent province, Become opening batter."}
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-4" data-testid={`active-goals-${horizon}`}>
        {active.map((g) => <GoalCard key={g.goal_id} g={g} onReload={onReload} onOpenMilestones={onOpenMilestones} chrome={chrome} />)}
      </div>
      {done.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Completed</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {done.map((g) => (
              <Card key={g.goal_id} className="border-slate-200 bg-slate-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle2 size={22} color="#10B981" />
                  {/* #251: `flex-1` alone still refuses to shrink below the
                      goal title's content width — the min-w-0 is what lets
                      it. */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 break-words">{g.target || g.goal_title}</div>
                    <div className="text-xs text-slate-500">Completed</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={async () => { if (confirm("Delete?")) { await api.delete(`/goals/${g.goal_id}`); onReload(); } }} className={chrome ? "h-11 w-11 flex-shrink-0" : undefined}>
                    <Trash2 size={14} />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GoalCard({ g, onReload, onOpenMilestones, chrome = false }) {
  const cat = CATEGORIES[g.category] || CATEGORIES.general;
  const CatIcon = cat.Icon;
  const status = normalizeGoalStatus(g.status);
  const statusMeta = goalStatusMeta(g.status);

  const updateProgress = async (val) => {
    const v = Number(val);
    const newStatus = v >= 100 ? "completed" : v > 0 ? "in_progress" : "not_started";
    await api.put(`/goals/${g.goal_id}`, { progress_percentage: v, status: newStatus });
    onReload();
  };
  const setStatus = async (s) => {
    await api.put(`/goals/${g.goal_id}`, { status: s, progress_percentage: s === "completed" ? 100 : g.progress_percentage || 0 });
    onReload();
  };
  const remove = async () => { if (confirm("Delete this goal?")) { await api.delete(`/goals/${g.goal_id}`); onReload(); } };

  // Issue #764 (D166 M1/M5/M7), fix round F1 — the tap affordance is the
  // TITLE ONLY, a real `<button>`, never the whole `Card`. The first cut
  // made the whole Card a `role="button"` with its own `onClick`/
  // `onKeyDown`, which (a) is invalid ARIA the moment an interactive
  // descendant — the delete button, the status `<Select>` — sits inside a
  // `role="button"`, and (b) actually broke keyboard use: Enter/Space on
  // `goal-del-{id}` or `goal-status-{id}` bubbled to the Card's own
  // handler and opened the step instead of deleting or changing status.
  // Review round 1's own journey addition (F7) reproduces exactly that
  // before this fix. `onOpenMilestones` is `undefined` whenever the
  // flag/role gate is off (`DevelopmentPlan`'s own `milestonesOn`), so the
  // button never renders at all on that leg — the flag-off render stays
  // byte-for-byte what it was before this issue, and no other control on
  // the card needs a stopPropagation wrapper any more (removed with this
  // fix: the whole-card click never bubbled through them in the first
  // place with the affordance gone from the Card itself).
  const openMilestones = onOpenMilestones ? () => onOpenMilestones(g.goal_id) : undefined;

  return (
    // `min-w-0` on the card (a child of the `active-goals-*` grid) and on
    // every shrinking link in the chain below it — issue #251. The goal
    // title and the "Assigned by <coach>" line are free text of unbounded
    // length, and the delete button on the right is fixed width, so without
    // this the card's min-content sets the grid track and the page scrolls.
    <Card className="min-w-0 border-slate-200 hover:shadow-md transition-all" data-testid={`goal-${g.goal_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${cat.color}15` }}>
              <CatIcon size={18} color={cat.color} />
            </div>
            <div className="min-w-0">
              {/* `break-words` alongside the `min-w-0` above — measured, not
                  assumed: with min-w-0 alone a title with no spaces in it
                  still painted 390px past the viewport. The class lets the
                  track shrink; this is what makes the text fit in it. */}
              {openMilestones ? (
                // Review round 2, F8 — the HEADING wraps the button, not the
                // other way round. A `<button>` may contain only phrasing
                // content, and ARIA makes a button's children presentational
                // regardless of their own tag — nesting `<h3>` inside it (the
                // first cut) risked a screen reader dropping the heading
                // entirely. `<h3><button>…</button></h3>` keeps the heading
                // real and gives the button its own text as its accessible
                // name.
                <h3 className="font-display font-bold text-lg text-slate-900 break-words">
                  <button
                    type="button"
                    onClick={openMilestones}
                    data-testid={`goal-open-milestones-${g.goal_id}`}
                    className="block w-full text-left bg-transparent border-0 p-0 m-0 cursor-pointer"
                  >
                    {g.target || g.goal_title}
                  </button>
                </h3>
              ) : (
                <h3 className="font-display font-bold text-lg text-slate-900 break-words">{g.target || g.goal_title}</h3>
              )}
              {(g.description || g.notes) && <p className="text-sm text-slate-500 break-words">{g.notes || g.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <Badge style={{ background: `${cat.color}15`, color: cat.color }}>{cat.label}</Badge>
                <Badge variant="outline">{g.horizon === "long" ? "Long-term" : "Short-term"}</Badge>
                {g.target_date && <Badge variant="outline">Due {g.target_date}</Badge>}
              </div>
              {/* Issue #190: the player can now tell a goal their coach set
                  for them from one they set themselves. Absent — not a
                  blank label — on their own goals, and on every goal that
                  predates authorship (both fields null). */}
              {g.created_by_coach_id && (
                <p className="text-xs text-slate-500 mt-2" data-testid={`goal-assigned-by-${g.goal_id}`}>
                  Assigned by {g.created_by_coach_name || "your coach"}
                </p>
              )}
            </div>
          </div>
          {/* #1164: on the new look the delete control has a 44px tap area (the icon stays 14px). */}
          <Button size="icon" variant="ghost" onClick={remove} data-testid={`goal-del-${g.goal_id}`} className={chrome ? "h-11 w-11 flex-shrink-0 -mt-2 -mr-2" : undefined}><Trash2 size={14} /></Button>
        </div>

        {/* Progress */}
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-slate-500">Progress</span>
            <span className="font-semibold text-slate-900">{g.progress_percentage || 0}%</span>
          </div>
          <Progress value={g.progress_percentage || 0} className="h-2" />
          <input type="range" min="0" max="100" value={g.progress_percentage || 0} onChange={(e) => updateProgress(e.target.value)} className="w-full mt-2 accent-[#0B2E59]" data-testid={`goal-slider-${g.goal_id}`} />
        </div>

        {/* Status */}
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-500">Status</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-40" data-testid={`goal-status-${g.goal_id}`}>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: statusMeta.color }} />
                {statusMeta.label}
              </span>
            </SelectTrigger>
            <SelectContent>
              {GOAL_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Actions */}
        {g.milestones?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Actions</div>
            <ul className="space-y-1.5">
              {g.milestones.map((m, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: "#D4A017" }} />{m}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- Coach feedback (issue #81) ----------------
// Read-only for the player: no edit/delete/pin controls anywhere in this
// section, unlike CoachPortal.jsx's mirror-image note list. The server
// backs this up independently — every note write route is role-gated to
// coach|admin (api/src/routes/notes.ts) — but the UI itself offers no
// mutation affordance to begin with.
// The new look renders components/PlayerCoachFeedback.jsx (#1157).
function CoachFeedbackPanel({ notes, hasCoach, failed, onRetry }) {
  // Issue #271: first, before anything else. The two empty states below are
  // both assertions ("nobody is watching" / "your coach hasn't written
  // anything"); neither may be reached on a failed read.
  if (failed) return <LoadFailureNote what="coach feedback" testId="feedback-error" onRetry={onRetry} />;

  // Pinned first; JS Array#sort is stable (ES2019+), so within the pinned
  // and unpinned groups the API's own newest-first ordering
  // (notesRoutes.get("/") in api/src/routes/notes.ts) is preserved exactly
  // — same technique CoachPortal.jsx uses for its own note list.
  const sorted = [...notes].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  if (sorted.length > 0) {
    return (
      <div className="space-y-3" data-testid="coach-feedback-list">
        {sorted.map((n) => <FeedbackNoteCard key={n.note_id} n={n} />)}
      </div>
    );
  }

  // hasCoach is still null while /players/me is in flight — render nothing
  // rather than guess, so we never flash the wrong one of the two empty
  // states below.
  if (hasCoach === null) return null;
  return hasCoach ? <NoFeedbackYetState /> : <NoCoachState />;
}

function FeedbackNoteCard({ n }) {
  const meta = NOTE_CATEGORIES[n.category] || NOTE_CATEGORIES.general;
  return (
    <Card className="border-slate-200" data-testid={`feedback-note-${n.note_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <Badge className="text-[10px]" style={{ background: `${meta.color}15`, color: meta.color }}>{meta.label}</Badge>
          {n.pinned && (
            <Badge variant="outline" className="text-[10px] flex items-center gap-1" style={{ color: "#D4A017", borderColor: "#D4A01750" }}>
              <Pin size={10} /> Pinned
            </Badge>
          )}
        </div>
        <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.feedback}</div>
        <div className="text-xs text-slate-500 mt-2">— {n.coach_name || "Your coach"} · {n.created_date?.slice(0, 10)}</div>
      </CardContent>
    </Card>
  );
}

// "No coach yet" and "no feedback yet" read very differently to a player —
// the former means "nobody's watching", the latter "someone is, just
// hasn't written anything down" — so they get distinct copy, not a shared
// generic empty state.
function NoCoachState() {
  return (
    <div className="text-center py-16" data-testid="feedback-empty-no-coach">
      <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "#64748B20" }}>
        <Users size={22} color="#64748B" />
      </div>
      <p className="font-semibold text-slate-900 mb-1">You don't have a coach yet</p>
      <p className="text-slate-500 max-w-sm mx-auto">Once a coach links to your profile, their feedback will show up here.</p>
    </div>
  );
}

function NoFeedbackYetState() {
  return (
    <div className="text-center py-16" data-testid="feedback-empty-no-feedback">
      <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "#D4A01720" }}>
        <NotebookPen size={22} color="#D4A017" />
      </div>
      <p className="font-semibold text-slate-900 mb-1">No coach feedback yet</p>
      <p className="text-slate-500 max-w-sm mx-auto">Check back after your next session — new feedback will appear here.</p>
    </div>
  );
}

// ---------------- New goal dialog ----------------
function NewGoalDialog({ onCreated, trigger }) {
  // Issue #79: `/development?new=1` opens this dialog on arrival — the
  // Development branch of the Dashboard's activity chooser. This component
  // is rendered above <Tabs> (never inside a TabsContent panel), so it is
  // mounted whichever tab the deep link carries.
  //
  // Issue #763 — its OWN `useCreateDialog()` instance, unchanged from
  // before this issue, DELIBERATELY NOT shared with `DevelopmentPlan`'s own
  // (which the new-screen gate reads) — see that component's own comment
  // for the bug sharing one instance caused (every `dev-new-goal` CLICK,
  // not just the URL, fed the gate and swapped the whole page mid-click).
  const [open, setOpen] = useCreateDialog();
  const [f, setF] = useState({ target: "", description: "", target_date: "", progress_percentage: 0,
    status: "not_started", horizon: "short", category: "general", milestones_text: "", notes: "" });

  const create = async () => {
    if (!f.target.trim()) return toast.error("Target is required");
    const payload = {
      goal_title: f.target,
      target: f.target,
      description: f.description,
      target_date: f.target_date || null,
      progress_percentage: Number(f.progress_percentage) || 0,
      status: f.status,
      horizon: f.horizon,
      category: f.category,
      notes: f.notes,
      milestones: f.milestones_text.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/goals", payload);
      toast.success("Goal created!");
      setOpen(false);
      setF({ target: "", description: "", target_date: "", progress_percentage: 0,
        status: "not_started", horizon: "short", category: "general", milestones_text: "", notes: "" });
      onCreated();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {/* Issue #940 (D175 P3) — the flag-on leg's own trigger (a navy
            `SecondaryAction`, rendered above the goal grid, never in a
            header) is passed in by the caller; every other caller (the
            flag-off leg, unchanged) gets the shipped `Button` below. */}
        {trigger ?? (
          <Button style={{ background: "#0B2E59" }} data-testid="dev-new-goal"><Plus size={16} className="mr-2" /> New goal</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create development goal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Target (measurable outcome) *</Label>
            <Input data-testid="goal-target" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} placeholder="e.g. Raise batting average to 45+" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Horizon</Label>
              <Select value={f.horizon} onValueChange={(v) => setF({ ...f, horizon: v })}>
                <SelectTrigger data-testid="goal-horizon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short-term (4–12 weeks)</SelectItem>
                  <SelectItem value="long">Long-term (6–18 months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="goal-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="tactical">Tactical</SelectItem>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="mental">Mental</SelectItem>
                  <SelectItem value="batting">Batting</SelectItem>
                  <SelectItem value="bowling">Bowling</SelectItem>
                  <SelectItem value="fielding">Fielding</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input data-testid="goal-date" type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="goal-status"><SelectValue /></SelectTrigger>
                <SelectContent>{GOAL_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Actions (one per line)</Label>
            <Textarea data-testid="goal-actions" rows={4} value={f.milestones_text} onChange={(e) => setF({ ...f, milestones_text: e.target.value })} placeholder="4x weekly net sessions&#10;Video review 10 innings&#10;Coach 1:1 fortnightly" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="goal-notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          <Button onClick={create} className="w-full" style={{ background: "#0B2E59" }} data-testid="goal-create-btn">Create goal</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- AI Plan panel ----------------
function AiPlanPanel({ onSaved }) {
  const [horizon, setHorizon] = useState("short");
  const [ambition, setAmbition] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cached, setCached] = useState(false);
  const [saving, setSaving] = useState(false);

  const run = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/development-plan/generate", {
        horizon,
        long_term_ambition: ambition || null,
        force_refresh: force,
      });
      setPlan(data.data); setCached(data.cached);
    } catch { toast.error("AI generation failed"); }
    setBusy(false);
  };

  const acceptAll = async () => {
    if (!plan) return;
    const items = [];
    for (const cat of ["technical", "tactical", "physical", "mental"]) {
      for (const o of plan[cat] || []) {
        items.push({
          category: cat, horizon,
          target: o.target, actions: o.actions || [],
          due_date: o.due_date, notes: o.notes || "",
        });
      }
    }
    if (!items.length) return toast.error("Nothing to save");
    setSaving(true);
    try {
      const { data } = await api.post("/development-plan/accept", items);
      toast.success(`Added ${data.created} objectives to your plan`);
      onSaved();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const acceptOne = async (cat, o) => {
    try {
      await api.post("/development-plan/accept", [{
        category: cat, horizon,
        target: o.target, actions: o.actions || [],
        due_date: o.due_date, notes: o.notes || "",
      }]);
      toast.success("Added to your plan");
      onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#D4A01720" }}>
              <Sparkles size={18} color="#D4A017" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg text-slate-900">AI-generated development plan</h2>
              <p className="text-sm text-slate-500">Analyses your matches, statistics, journal entries and position to craft a personalised plan across four dimensions.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div>
              <Label>Horizon</Label>
              <Select value={horizon} onValueChange={setHorizon}>
                <SelectTrigger data-testid="ai-horizon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short-term (4–12 weeks)</SelectItem>
                  <SelectItem value="long">Long-term (6–18 months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Long-term ambition (optional)</Label>
              <Input value={ambition} onChange={(e) => setAmbition(e.target.value)} placeholder="e.g. Make First XI · Represent province · Become opening batter" data-testid="ai-ambition" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => run(false)} disabled={busy} style={{ background: "#0B2E59" }} data-testid="ai-generate">
              {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating…</> : <><Sparkles size={16} className="mr-2" /> Generate plan</>}
            </Button>
            {plan && <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="ai-refresh"><RotateCw size={14} className="mr-2" /> Refresh</Button>}
            {plan && <Button onClick={acceptAll} disabled={saving} style={{ background: "#D4A017" }} data-testid="ai-accept-all">
              {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Adding…</> : <><Check size={16} className="mr-2" /> Add all to my plan</>}
            </Button>}
          </div>
        </CardContent>
      </Card>

      {plan && (
        <div className="space-y-6" data-testid="ai-plan-output">
          {cached && <Badge variant="outline" className="text-[10px]">cached</Badge>}
          {plan.summary && (
            <Card className="border-slate-200 overflow-hidden">
              <div className="p-6 border-l-4" style={{ borderColor: "#D4A017", background: "linear-gradient(90deg, #FEF9E7 0%, transparent 100%)" }}>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Sparkles size={12} color="#D4A017" /> Positioning</div>
                <p className="text-slate-800">{plan.summary}</p>
              </div>
            </Card>
          )}

          <div className="grid gap-6">
            {["technical", "tactical", "physical", "mental"].map((cat) => {
              const list = plan[cat] || [];
              if (!list.length) return null;
              const c = CATEGORIES[cat];
              const CatIcon = c.Icon;
              return (
                <Card key={cat} className="border-slate-200" data-testid={`ai-cat-${cat}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${c.color}20` }}>
                        <CatIcon size={18} color={c.color} />
                      </div>
                      <h3 className="font-display font-bold text-lg text-slate-900">{c.label} objectives</h3>
                      <Badge variant="outline" className="ml-auto">{list.length}</Badge>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {list.map((o, i) => (
                        <div key={i} className="min-w-0 p-4 rounded-xl border border-slate-200 flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-2">
                            {/* #251: model-written text of unbounded length
                                beside a fixed-width Add button. */}
                            <div className="min-w-0 font-semibold text-slate-900 break-words">{o.target}</div>
                            <Button size="sm" variant="outline" onClick={() => acceptOne(cat, o)} data-testid={`ai-accept-${cat}-${i}`}>
                              <Plus size={12} className="mr-1" /> Add
                            </Button>
                          </div>
                          {o.notes && <p className="text-xs text-slate-500">{o.notes}</p>}
                          {o.actions?.length > 0 && (
                            <div className="mt-1">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Actions</div>
                              <ul className="space-y-1">
                                {o.actions.map((a, j) => (
                                  <li key={j} className="text-sm text-slate-700 flex gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: c.color }} />{a}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {o.due_date && <div className="text-xs text-slate-500 mt-1">Due {o.due_date}</div>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {!plan && !busy && (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "#D4A01720" }}>
            <Sparkles size={22} color="#D4A017" />
          </div>
          <p className="text-slate-500">Generate a personalised, evidence-backed development plan.</p>
        </div>
      )}
    </div>
  );
}
